import React, {useEffect, useRef, useState} from "react";
import {Alert, Button, Descriptions, Divider, Popconfirm, Progress, Select, Table, message} from "antd";
import {CheckOutlined, LoadingOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import api from "../api";
import {handleApiError} from "../helpers/formatApiError";
import useConsumer from "../../channels/consumer";
import {standardMetadataFieldLabel, standardMetadataFieldsForKeys} from "./standardFields";

export function metadataEntityTypeFields(metadataEntityType) {
    const fields = [];
    const ids = new Set();
    const addField = field => {
        if (!field || ids.has(field.id)) return;
        ids.add(field.id);
        fields.push(field);
    };

    metadataEntityType?.custom_meta_groups?.forEach(groupMembership => {
        groupMembership.custom_meta_group?.custom_meta_fields?.forEach(fieldMembership => {
            addField(fieldMembership.custom_meta_field);
        });
    });

    metadataEntityType?.custom_meta_fields?.forEach(fieldMembership => {
        addField(fieldMembership.custom_meta_field);
    });

    return fields;
}

export function metadataEntityMappingValue(column) {
    const mapping = column.mapping;
    if (!mapping) return "raw";

    if (mapping.type === "custom_meta_field") return `custom_meta_field:${mapping.custom_meta_field_id}`;
    if (mapping.type === "standard_metadata_field") return `standard_metadata_field:${mapping.standard_metadata_field}`;
    if (mapping.type === "tag_field") return `tag_field:${mapping.tag_field}`;
    return mapping.type;
}

export function metadataEntityMappingOptions(metadataEntityType, t) {
    const fields = metadataEntityTypeFields(metadataEntityType);
    const standardFields = standardMetadataFieldsForKeys(metadataEntityType?.standard_field_keys);
    const tagFields = [
        ["name", t("name", "Name")],
        ["list", t("list", "List")],
        ["description", t("description", "Description")],
        ["link", t("link", "Link")]
    ];

    return [
        {value: "raw", label: t("ignore-source-field", "Ignore source field")},
        {value: "taxonomy_path", label: t("taxonomy-path", "Taxonomy Path")},
        ...tagFields.map(([field, label]) => ({
            value: `tag_field:${field}`,
            label: `${t("standard-tag-field", "Standard tag field")}: ${label}`
        })),
        ...standardFields.map(field => ({
            value: `standard_metadata_field:${field.key}`,
            label: `${t("standard-metadata-field", "Standard metadata field")}: ${t(field.key, field.label)}`
        })),
        ...fields.map(field => ({
            value: `custom_meta_field:${field.id}`,
            label: `${t("structured-field", "Structured field")}: ${field.name}`
        }))
    ];
}

export function decodeMetadataEntityMappingValue(metadataEntityType, value) {
    if (value === "raw") return null;
    if (value === "taxonomy_path") return {type: "taxonomy_path"};

    const [type, id] = value.split(":");
    if (type === "tag_field") return {type, tag_field: id};
    if (type === "standard_metadata_field") {
        return {
            type,
            standard_metadata_field: id,
            standard_metadata_field_label: standardMetadataFieldLabel(id)
        };
    }
    if (type === "custom_meta_field") {
        const field = metadataEntityTypeFields(metadataEntityType).find(field => field.id === parseInt(id, 10));
        return {
            type,
            custom_meta_field_id: parseInt(id, 10),
            custom_meta_field_name: field?.name
        };
    }

    return null;
}

export function metadataEntityMappingLabel(metadataEntityType, column, t) {
    const value = metadataEntityMappingValue(column);
    return metadataEntityMappingOptions(metadataEntityType, t).find(option => option.value === value)?.label;
}

export default function MetadataEntityImportSetup({metadataImport, onDone}) {
    const {t} = useTranslation();
    const [attrs, setAttrs] = useState(metadataImport);
    const [progress, setProgress] = useState(0);
    const [processing, setProcessing] = useState(false);
    const [starting, setStarting] = useState(false);

    const consumer = useConsumer();
    const subscription = useRef();

    useEffect(() => {
        api.get(`/api/metadata_entity_imports/${metadataImport.id}`).then(res => {
            setAttrs(prev => ({...prev, ...res.data}));
        }).catch(handleApiError);

        if (consumer && !subscription.current) {
            subscription.current = consumer.subscriptions.create({channel: "UserChannel"}, {
                received: data => {
                    switch (data.type) {
                        case "metadataEntityImport":
                            if (data.attrs?.id === metadataImport.id) setAttrs(prev => ({...prev, ...data.attrs}));
                            return;

                        case "metadataEntityImportProgress":
                            if (data.id === metadataImport.id) {
                                if (typeof data.progress === "number") setProgress(data.progress);
                                if (data.done) {
                                    message.success(t("message-processing-done", "Processing Done!"));
                                    onDone && onDone();
                                }
                            }
                            return;
                    }
                }
            });
        }

        return () => {
            subscription.current?.unsubscribe();
            subscription.current = null;
        };
    }, [metadataImport.id, consumer]);

    const updateColumnMapping = (column, value) => {
        api.put(`/api/metadata_entity_imports/${metadataImport.id}/mapping`, {
            column: {
                ...column,
                mapping: decodeMetadataEntityMappingValue(attrs.metadata_entity_type, value)
            }
        }).then(res => {
            message.success(t("column-mapping-updated", "Column mapping updated."));
            setAttrs(prev => ({...prev, ...res.data}));
        }).catch(handleApiError);
    };

    const startProcessing = () => {
        setStarting(true);
        api.post(`/api/metadata_entity_imports/${metadataImport.id}/start_process`).then(() => {
            setStarting(false);
            setProcessing(true);
        }).catch(error => {
            setStarting(false);
            handleApiError(error);
        });
    };

    if (processing) {
        return (
            <>
                <LoadingOutlined/> {t("processing-progress", "Processing progress")}:
                <Progress percent={progress || 0} status={"active"}/>
            </>
        );
    }

    if (attrs.aasm_state === "errored") {
        const error = attrs.parse_error || attrs.processing_error;
        return (
            <Alert
                type={"error"}
                showIcon
                message={attrs.parse_error ? t("parse-error", "Parse Error") : t("processing-error", "Processing Error")}
                description={`${t("import-error-description", "Unable to import file, please try another.")}${error?.message ? ` (${error.message})` : ""}`}
            />
        );
    }

    return (
        <>
            {(attrs.aasm_state === "analyzing" || attrs.aasm_state === "pending") && (
                <Alert
                    style={{marginBottom: "2em"}}
                    type={"info"}
                    showIcon
                    message={<em><LoadingOutlined/> {t("analyzing", "Analyzing")}...</em>}
                />
            )}

            <Descriptions bordered size="small" column={1}>
                <Descriptions.Item label={t("filename", "Filename")}>{attrs.filename}</Descriptions.Item>
                <Descriptions.Item label={t("metadata-entity-type", "Metadata Entity Type")}>{attrs.metadata_entity_type?.name}</Descriptions.Item>
                <Descriptions.Item label={t("rows", "Rows")}>{attrs.rows}</Descriptions.Item>
                <Descriptions.Item label={t("matching-existing-entities", "Matching existing entities")}>{attrs.matches}</Descriptions.Item>
            </Descriptions>

            <Divider/>

            <Table
                rowKey={column => `${attrs.id}-${column.index}-${column.name}`}
                size="small"
                dataSource={attrs.columns || []}
                pagination={false}
                columns={[
                    {
                        title: t("source-column", "Source Column"),
                        dataIndex: "name"
                    },
                    {
                        title: t("mapping", "Mapping"),
                        render: (_, column) => (
                            <Select
                                style={{width: "100%"}}
                                value={metadataEntityMappingValue(column)}
                                options={metadataEntityMappingOptions(attrs.metadata_entity_type, t)}
                                onChange={value => updateColumnMapping(column, value)}
                                optionFilterProp="label"
                                showSearch
                            />
                        )
                    }
                ]}
            />

            <Popconfirm title={t("confirm-process-import", "Process import?")} onConfirm={startProcessing}>
                <Button
                    icon={<CheckOutlined/>}
                    type={"primary"}
                    block
                    style={{marginTop: "2em"}}
                    loading={starting}
                    disabled={!["analyzed", "processed"].includes(attrs.aasm_state)}
                >
                    {t("button-process-import", "Process Import")}
                </Button>
            </Popconfirm>
        </>
    );
}
